// dependencies
import { format, isValid, parseISO } from "date-fns";
import React from "react";
import { z } from "zod";

import { Avatar, AvatarProfile } from "@performyard/py-components";
import { GetEmployeeDetailsResponse } from "@performyard/service-contracts";

// components
import Container from "../../../components/Container";
import EmployeePicker from "../../../components/EmployeePicker";
import GroupPicker from "../../../components/GroupPicker";
import DatePicker from "../../../components/Inputs/DatePicker";
import Input from "../../../components/Inputs/InputText";
import LongText from "../../../components/Inputs/LongText";
import TagList from "../../../components/Inputs/TagList";
import Link from "../../../components/Link";
import Typography from "../../../components/Text";

//utils
import toEmployeeUrl from "../../../utils/URLs/Employee";
import { addTimezoneOffset } from "../../../utils/dateUtils";

//config
import Config from "../Config";

export const getViewableField = (fieldType: string, value: any) => {
	if (value == null) return "";
	switch (fieldType) {
		case "date": {
			const parsedDate = parseISO(value as string);
			if (!isValid(parsedDate)) {
				return value as string;
			}
			return format(addTimezoneOffset(parsedDate), "MMMM d, yyyy");
		}
		case "person":
			return <AvatarProfile employeeId={value?._id} employeeName={value?.name} avatarSize={20} />;
		case "people":
			return (
				<Container wrap>
					{value?.map((person) => (
						<AvatarProfile
							key={person._id}
							employeeId={person._id}
							employeeName={person.name}
							avatarSize={20}
						/>
					))}
				</Container>
			);
		case "multi-select":
			return typeof value === "string" ? value : value.join(", ");
		case "department":
			return (
				<Link style={{ minWidth: 0, padding: 0 }} href={`/group/employees/${value._id}`}>
					{value.name}
				</Link>
			);
		default:
			return value as string;
	}
};

export const getValueForInputType = (fieldType: string, value: any) => {
	switch (fieldType) {
		case "person":
			if (!value) return null;
			return { value: value?._id, label: value?.name };
		case "people":
			return value?.map((person) => ({ value: person._id, label: person.name }));
		case "department":
			if (value === undefined) return null;
			return { id: value._id, name: value.name };
		default:
			return value ?? null;
	}
};

type getFieldInputOptions = {
	disabled: boolean;
	labelHref?: string;
	labelTooltip?: string;
};

export const getIntegrationImage = (fieldIntegrationMap = {}) => {
	const integrations = Object.keys(fieldIntegrationMap).filter((key) => fieldIntegrationMap[key]);
	if (integrations?.length === 0) return null;
	const integration = integrations[0];

	return `${Config.imgBasePath}${Config.INTEGRATIONS_IMAGE_MAP[integration]}`;
};

const TITLE_TOOLTIP = "View Field History";

export const getFieldInput = (
	fieldData,
	value: any,
	onInputChange: ({ fieldValue }) => void,
	showValidationError: boolean,
	isEmployeeManagedByIntegration: boolean,
	label = "Field Value",
	options: getFieldInputOptions = { disabled: false }
) => {
	const fieldType = fieldData?.type;

	const fieldIntegration =
		isEmployeeManagedByIntegration && (getIntegrationImage(fieldData?.integrations) || undefined);
	const fieldScimManaged = fieldData?.isScimManaged && `${Config.imgBasePath}${Config.INTEGRATIONS_IMAGE_MAP.scim}`;

	switch (fieldType) {
		case "text":
			return (
				<Input
					testId="text-input"
					label={label}
					placeholder="Enter new value"
					value={value}
					onChange={(value) => onInputChange({ fieldValue: value })}
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "date":
			return (
				<DatePicker
					label={label}
					value={value ? addTimezoneOffset(value) : ""}
					onChange={(value) => {
						onInputChange({ fieldValue: value ? value : null });
					}}
					hasIcon
					errorMessage={
						showValidationError ? 'Invalid date format. Please use "MM/DD/YYYY" format.' : undefined
					}
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "phone":
			return (
				<Input
					testId="phone-input"
					label={label}
					placeholder="Enter new value"
					value={value}
					onChange={(value) => onInputChange({ fieldValue: value })}
					errorMessage={showValidationError ? "Invalid phone number" : ""}
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "person":
			return (
				<>
					<EmployeePicker
						label={label}
						value={value || undefined}
						onChange={(value) => onInputChange({ fieldValue: value })}
						disabled={options.disabled || !!fieldIntegration}
						titleHref={options.labelHref}
						titleImage={fieldIntegration || fieldScimManaged}
						isClearable
						titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					/>
				</>
			);
		case "people":
			return (
				<EmployeePicker
					label={label}
					value={value || []}
					onChange={(value) => onInputChange({ fieldValue: value })}
					isMultiSelect
					exclusionList={fieldData?.name.toLowerCase() === "contributors" ? [fieldData?.user?._id || ""] : []}
					draggableOptions
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "email":
			return (
				<Input
					testId="email-input"
					label={label}
					placeholder="Enter new value"
					value={value}
					onChange={(value) => onInputChange({ fieldValue: value })}
					errorMessage={showValidationError ? "Invalid email" : ""}
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "department":
			return (
				<GroupPicker
					label={label}
					value={value}
					onChange={(value) => onInputChange({ fieldValue: value })}
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					clearable
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "numeric":
			return (
				<Input
					testId="numeric-input"
					type="number"
					label={label}
					placeholder="Enter new value"
					value={value}
					onChange={(value) => onInputChange({ fieldValue: value })}
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "salary":
			return (
				<Input
					testId="salary-input"
					type="number"
					min={0}
					max={10_000_000_000}
					label={label}
					placeholder="Enter new value"
					value={value}
					onChange={(value) => onInputChange({ fieldValue: value })}
					disabled={options.disabled || !!fieldIntegration}
					errorMessage={showValidationError ? "Salary must be between 0 and 10,000,000,000" : ""}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "monetary":
			return (
				<Input
					testId="monetary-input"
					type="number"
					label={label}
					placeholder="Enter new value"
					value={value}
					onChange={(value) => onInputChange({ fieldValue: value })}
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "single-select":
			return (
				<TagList
					inputId="single-select-input"
					label={label}
					options={fieldData?.options?.map((option) => ({ value: option, label: option }))}
					value={value ? value[0] : null}
					onChange={(value) => onInputChange({ fieldValue: value ? [value] : null })}
					disabled={options.disabled || !!fieldIntegration}
					animatedActive
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "multi-select":
			return (
				<TagList
					inputId="multi-select-input"
					label={label}
					options={fieldData?.options?.map((option) => ({ value: option, label: option }))}
					value={value || []}
					onChange={(value) => onInputChange({ fieldValue: value })}
					multiple
					disabled={options.disabled || !!fieldIntegration}
					animatedActive
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		case "textbox":
			return (
				<LongText
					label={label}
					value={value}
					onChange={(value) => onInputChange({ fieldValue: value })}
					style={{ minHeight: 80, resize: "vertical" }}
					disabled={options.disabled || !!fieldIntegration}
					titleHref={options.labelHref}
					titleTooltip={options.labelHref ? TITLE_TOOLTIP : undefined}
					titleImage={fieldIntegration || fieldScimManaged}
				/>
			);
		default:
			return null;
	}
};

export const getValueForUpdate = (fieldType: string, fieldValue: any) => {
	switch (fieldType) {
		case "person":
			return fieldValue?.value || null;
		case "people":
			return fieldValue?.map((person) => person?.value);
		case "department":
			return fieldValue?.id || null;
		default:
			return fieldValue;
	}
};

export const getEditEmployeeCustomFieldsSchema = (customFields: GetEmployeeDetailsResponse["customFields"]) =>
	z.object({
		// map the custom fields to the zod types
		customFields: z.object(
			Object.fromEntries(
				customFields?.map((field) => {
					return [field._id, Config.inputSchema[field.type].optional().nullable()];
				})
			)
		),
	});

export const getCustomFieldDisplayValue = (fieldType: string, value: any) => {
	switch (fieldType) {
		case "person":
			return (
				<Container gap={"4px"} verticalAlignment="center">
					<Avatar size={24} id={value?._id} name={value?.name || "User"} />
					<Link href={toEmployeeUrl(value?._id)}>
						<Typography size="paragraph-3" weight="regular" element="span">
							{value?.name}
						</Typography>
					</Link>
				</Container>
			);
		case "people":
			return (
				<Container gap={"8px"} wrap>
					{value?.map((person) => (
						<Container gap={"4px"} key={person._id} verticalAlignment="center">
							<Avatar size={24} id={person._id} name={person.name} />
							<Link href={toEmployeeUrl(person?._id)}>
								<Typography size="paragraph-3" weight="regular" element="span">
									{person?.name}
								</Typography>
							</Link>
						</Container>
					))}
				</Container>
			);
		case "department":
			return (
				<Typography size="paragraph-3" weight="regular" element="span" overflowWrap="break-word">
					{value?.name}
				</Typography>
			);
		case "date":
			if (!value) return null;

			if (isValid(parseISO(value))) {
				//parse date to we strip the timezone info
				const parsedDate = parseISO(value);
				return (
					<Typography size="paragraph-3" weight="regular" element="span">
						{format(addTimezoneOffset(parsedDate), "MMMM d, yyyy")}
					</Typography>
				);
			}
			break;
		default:
			return (
				<Typography size="paragraph-3" weight="regular" element="span" overflowWrap="break-word">
					{Array.isArray(value) ? value.join(", ") : value}
				</Typography>
			);
	}
};
